"use client";

import React, {useCallback, useRef} from "react";
import {Box, Container, Typography} from "@mui/material";
import {Theme, useTheme} from "@mui/material/styles";
import FaIcon from "@/components/utilities/fa-icon";
import {usePathname, useRouter} from "next/navigation";
import {BreadcrumbProps} from "@/types/breadcrumbs";

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
                                                          items,
                                                          fallback,
                                                          maxWidth,
                                                          onlyMobile,
                                                      }) => {
    const theme = useTheme<Theme>();
    const router = useRouter();
    const pathname = usePathname();

    const navigatingRef = useRef(false);

    const safeReplace = useCallback(
        (href: string) => {
            if (typeof window === "undefined") return;

            const target = String(href || "").trim();
            if (!target) return;

            if (target === window.location.pathname) return;

            router.replace(target);
        },
        [router]
    );

    const handleBack = useCallback(() => {
        if (typeof window === "undefined") return;
        if (navigatingRef.current) return;

        navigatingRef.current = true;

        if (window.history.length > 2) {
            router.back();
            window.setTimeout(() => {
                navigatingRef.current = false;
            }, 600);
            return;
        }

        const backItem = items?.find((i) => i.back && i.href)?.href;
        if (backItem) {
            safeReplace(backItem);
            navigatingRef.current = false;
            return;
        }

        if (fallback) {
            safeReplace(fallback);
            navigatingRef.current = false;
            return;
        }

        safeReplace("/");
        navigatingRef.current = false;
    }, [items, fallback, router, safeReplace]);

    const BackRow = ({mobile}: { mobile: boolean }) => (
        <Box
            width = "100%"
            display={{xs: mobile ? "block" : "none", sm: mobile ? "none" : "block"}}
            sx = {{
                ...(mobile
                    ? {
                        mt: 0,
                    }
                    : {position: "relative", zIndex: 1}),
            }}
        >
            <Container
                maxWidth = {maxWidth}
                sx = {{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    py:1.6,
                    pb:3
                }}
            >
                <Box
                    display = "flex"
                    alignItems = "center"
                    width = "fit-content"
                    gap = {1}
                    onClick = {handleBack}
                    color = {mobile ? theme.palette.grey[500] : theme.palette.primary.main}
                    sx = {{
                        cursor: "pointer",
                        userSelect: "none",
                    }}
                >
                    <FaIcon fontSize = {mobile ? 14 : 16} faIcon = "fa-arrow-right" variant = "solid" />
                    <Typography fontSize = {14} >بازگشت به صفحه قبلی</Typography >
                </Box >
            </Container >
        </Box >
    );

    return (
        <>
            <BackRow mobile />
            {!onlyMobile && <BackRow mobile = {false} />}
        </>
    );
};